/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
}

function queryFocusable(container: HTMLElement | null): HTMLElement[] {
  if (container === null) {
    return [];
  }
  const selector =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * Modal dialog in a portal with focus trap and TrueRead-sized controls (via CSS tokens).
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  closeOnEsc = true,
  closeOnOverlayClick = true,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialogEl = dialogRef.current;
    if (dialogEl === null) return;

    const focusInitial = () => {
      const nodes = queryFocusable(dialogEl);
      (nodes[0] ?? dialogEl).focus();
    };

    const frame = globalThis.requestAnimationFrame(() => {
      focusInitial();
    });

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const nodes = queryFocusable(dialogRef.current);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }

    dialogEl.addEventListener('keydown', onKeyDown);

    return () => {
      globalThis.cancelAnimationFrame(frame);
      dialogEl.removeEventListener('keydown', onKeyDown);
      queueMicrotask(() => {
        previous?.focus?.();
      });
    };
  }, [open]);

  useEffect(() => {
    if (!open || closeOnEsc === false) return;
    function onDocKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', onDocKey);
    return () => {
      document.removeEventListener('keydown', onDocKey);
    };
  }, [open, closeOnEsc, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="ecs-modal-overlay"
      role="presentation"
      onClick={() => {
        if (closeOnOverlayClick) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="ecs-modal-dialog"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="ecs-modal-header">
          <h2 id={titleId} className="ecs-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="ecs-modal-close"
            aria-label="Close"
            onClick={() => {
              onClose();
            }}
          >
            <svg
              width="1.25em"
              height="1.25em"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className="ecs-modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
